export const DUNGEON_SHOP_CONFIG_VERSION = 1 as const;

export type DungeonMonsterShopEntry = {
  monsterId: string;
  goldPricePerUnit: number;
  cashPricePerUnit: number;
  /** ซื้อด้วย gold (coins) ได้สูงสุดต่อด่าน */
  goldMaxUnits: number;
};

export type DungeonShopConfig = {
  version: typeof DUNGEON_SHOP_CONFIG_VERSION;
  powerStep: number;
  powerGoldPricePerStep: number;
  powerCashPricePerStep: number;
  goldMaxPowerMultiplier: number;
  cashMaxPowerMultiplier: number;
  defaultMonsterGoldPrice: number;
  defaultMonsterCashPrice: number;
  defaultMonsterGoldMax: number;
  monsters: DungeonMonsterShopEntry[];
  updatedAt: number;
};

export type DungeonMonsterAllowance = Record<
  string,
  { gold: number; cash: number }
>;

export function createDefaultDungeonShopConfig(): DungeonShopConfig {
  return {
    version: DUNGEON_SHOP_CONFIG_VERSION,
    powerStep: 0.5,
    powerGoldPricePerStep: 500,
    powerCashPricePerStep: 10,
    goldMaxPowerMultiplier: 20,
    cashMaxPowerMultiplier: 1000,
    defaultMonsterGoldPrice: 100,
    defaultMonsterCashPrice: 5,
    defaultMonsterGoldMax: 100,
    monsters: [],
    updatedAt: 0,
  };
}

export function normalizeDungeonShopConfig(raw: unknown): DungeonShopConfig {
  const base = createDefaultDungeonShopConfig();
  if (!raw || typeof raw !== "object") return base;
  const p = raw as Partial<DungeonShopConfig>;
  const monsters = Array.isArray(p.monsters)
    ? p.monsters
        .filter((m) => m && typeof m === "object" && typeof (m as DungeonMonsterShopEntry).monsterId === "string")
        .map((m) => {
          const e = m as DungeonMonsterShopEntry;
          return {
            monsterId: e.monsterId,
            goldPricePerUnit: clamp(e.goldPricePerUnit, 0, 1_000_000, base.defaultMonsterGoldPrice),
            cashPricePerUnit: clamp(e.cashPricePerUnit, 0, 1_000_000, base.defaultMonsterCashPrice),
            goldMaxUnits: clamp(e.goldMaxUnits, 0, 100_000, base.defaultMonsterGoldMax),
          };
        })
    : [];
  return {
    version: DUNGEON_SHOP_CONFIG_VERSION,
    powerStep: clamp(p.powerStep, 0.1, 10, base.powerStep),
    powerGoldPricePerStep: clamp(p.powerGoldPricePerStep, 0, 1_000_000, base.powerGoldPricePerStep),
    powerCashPricePerStep: clamp(p.powerCashPricePerStep, 0, 1_000_000, base.powerCashPricePerStep),
    goldMaxPowerMultiplier: clamp(p.goldMaxPowerMultiplier, 1, 1000, base.goldMaxPowerMultiplier),
    cashMaxPowerMultiplier: clamp(p.cashMaxPowerMultiplier, 1, 5000, base.cashMaxPowerMultiplier),
    defaultMonsterGoldPrice: clamp(p.defaultMonsterGoldPrice, 0, 1_000_000, base.defaultMonsterGoldPrice),
    defaultMonsterCashPrice: clamp(p.defaultMonsterCashPrice, 0, 1_000_000, base.defaultMonsterCashPrice),
    defaultMonsterGoldMax: clamp(p.defaultMonsterGoldMax, 0, 100_000, base.defaultMonsterGoldMax),
    monsters,
    updatedAt: typeof p.updatedAt === "number" ? p.updatedAt : 0,
  };
}

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

export function resolveMonsterShopEntry(
  config: DungeonShopConfig,
  monsterId: string,
): DungeonMonsterShopEntry {
  const found = config.monsters.find((m) => m.monsterId === monsterId);
  if (found) return found;
  return {
    monsterId,
    goldPricePerUnit: config.defaultMonsterGoldPrice,
    cashPricePerUnit: config.defaultMonsterCashPrice,
    goldMaxUnits: config.defaultMonsterGoldMax,
  };
}

export function normalizeMonsterAllowance(raw: unknown): DungeonMonsterAllowance {
  if (!raw || typeof raw !== "object") return {};
  const out: DungeonMonsterAllowance = {};
  for (const [id, val] of Object.entries(raw)) {
    if (!val || typeof val !== "object") continue;
    const v = val as { gold?: unknown; cash?: unknown };
    out[id] = {
      gold: Math.max(0, Math.floor(typeof v.gold === "number" ? v.gold : 0)),
      cash: Math.max(0, Math.floor(typeof v.cash === "number" ? v.cash : 0)),
    };
  }
  return out;
}

export function allowanceTotal(allowance: DungeonMonsterAllowance, monsterId: string): number {
  const a = allowance[monsterId];
  if (!a) return 0;
  return a.gold + a.cash;
}
